'use client';

import * as React from 'react';
import { Select as SelectPrimitive } from 'radix-ui';

import { cn } from '@/lib/core';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" className={cn('p-0', className)} {...props} />;
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        'bg-jncquoi-neutral-00 flex h-10 w-full items-center justify-between rounded-none',
        'border-jncquoi-neutral-200 border-[0.5px] border-solid',
        'px-3.75 py-2',
        'font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-medium tracking-[0.5px] uppercase',
        'data-placeholder:text-jncquoi-neutral-600',
        'focus-visible:border-jncquoi-neutral-900 focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-jncquoi-asia-7427c-500',
        'transition-colors select-none',
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="text-jncquoi-neutral-1000 pointer-events-none size-4 shrink-0" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  align = 'start',
  withoutPortal = false,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  withoutPortal?: boolean;
}) {
  const content = (
    <SelectPrimitive.Content
      data-slot="select-content"
      className={cn(
        'bg-jncquoi-neutral-00 relative z-50 max-h-(--radix-select-content-available-height) overflow-y-auto rounded-none',
        'border-jncquoi-neutral-200 border border-solid',
        'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 duration-100',
        position === 'popper' &&
          'w-(--radix-select-trigger-width) data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      align={align}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  );

  if (withoutPortal) return content;
  return <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal>;
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn(
        'font-pp-neue-montreal text-jncquoi-neutral-800 px-3.75 py-2 text-[12px] tracking-[0.5px] uppercase',
        className
      )}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default items-center outline-none select-none',
        'border-b-jncquoi-neutral-200 border-b border-solid last:border-b-0',
        'px-3.75 py-3',
        'font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-medium tracking-[0.5px] uppercase',
        'hover:bg-jncquoi-neutral-50 focus:bg-jncquoi-neutral-50',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-jncquoi-neutral-200 pointer-events-none my-0 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'bg-jncquoi-neutral-00 z-10 flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'bg-jncquoi-neutral-00 z-10 flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
